// myBRE notices (ADR 0064): Hub tells myBRE about board, committee,
// nomination and volunteer work for one person, and myBRE puts it in that
// person's one inbox (the bell, the mode badges, the phone push the person
// chose). myBRE owns the inbox and writes every word; Hub sends a type, a
// few facts, the myBRE page it points to, and what it is about.
//
// Hub keeps an outbox (core `mybre_notice`, migration 0080), modeled on the
// governance email outbox: while board notifications are paused (ADR 0052)
// every board, committee and nomination notice is written but held, and
// turning notifications back on releases only what is still current.
// Volunteer notices aren't board notifications and are never held.
//
//   POST {MYBRE_NOTICES_URL}/api/internal/hub/v1/notify
//   canonical = ['bre-hub-mybre-notify-v1', 'POST', path, channel, timestamp, nonce, sha256Hex(body)].join('\n')
//   x-hub-notify-signature = base64url-no-padding(HMAC-SHA256(secret, canonical))
//   headers x-hub-notify-channel ('board' | 'volunteer'), -timestamp, -nonce
//
// The secret is the channel's own, already shared with myBRE's web Worker:
// HUB_BOARD_SECRET (board, committees, nomination) or HUB_VOLUNTEER_SECRET
// (volunteer). myBRE refuses a notice of one channel carrying the other's
// types or subjects, so a leaked volunteer key can't reach a director.
// myBRE's own copy is bre-loyalty lib/hub-notify.ts; the test vector in
// apps/api/test/mybre-notices.test.ts keeps the two honest.

import { sha256Hex } from './volunteer-portal'

export const MYBRE_NOTIFY_PATH = '/api/internal/hub/v1/notify'
export const MYBRE_NOTIFY_SIGNATURE_VERSION = 'bre-hub-mybre-notify-v1'
export const MYBRE_NOTIFY_HEADERS = { channel: 'x-hub-notify-channel', timestamp: 'x-hub-notify-timestamp', nonce: 'x-hub-notify-nonce', signature: 'x-hub-notify-signature' } as const
export const MYBRE_NOTIFY_BATCH_MAX = 50

// The system actor that claims and reports myBRE notices.
export const MYBRE_NOTICES_SYSTEM_SUB = 'system:mybre-notices'

export type MybreNoticeChannel = 'board' | 'volunteer'

// Every type Hub sends, with its channel. myBRE's inbox-types.ts lists the
// same ones (with the switch labels and push defaults the owner chose).
export const MYBRE_NOTICE_TYPES = {
  'board.meeting_notice': 'board',
  'board.meeting_changed': 'board',
  'board.meeting_canceled': 'board',
  'board.packet': 'board',
  'board.vote_open': 'board',
  'board.vote_closing': 'board',
  'board.minutes_review': 'board',
  'board.minutes_sign': 'board',
  'board.disclosure_due': 'board',
  'board.email_meeting_to_send': 'board',
  'committees.meeting_notice': 'board',
  'committees.meeting_changed': 'board',
  'committees.meeting_canceled': 'board',
  'committees.packet': 'board',
  'committees.vote_open': 'board',
  'committees.vote_closing': 'board',
  'committees.minutes_review': 'board',
  'committees.minutes_sign': 'board',
  'committees.disclosure_due': 'board',
  'committees.recommendation': 'board',
  'nomination.asked': 'board',
  'volunteer.shift_confirmed': 'volunteer',
  'volunteer.shift_changed': 'volunteer',
  'volunteer.shift_canceled': 'volunteer',
  'volunteer.shift_reminder': 'volunteer',
  'volunteer.qualification_verified': 'volunteer',
  'volunteer.qualification_rejected': 'volunteer',
  'volunteer.qualification_expiring': 'volunteer',
  'volunteer.hours_approved': 'volunteer',
  // ADR 0076: the week's open shifts a volunteer can sign up for.
  'volunteer.open_shifts': 'volunteer',
} as const satisfies Record<string, MybreNoticeChannel>

export type MybreNoticeType = keyof typeof MYBRE_NOTICE_TYPES

// Subject keys start with the channel's prefix (myBRE checks it).
export const MYBRE_SUBJECT_PREFIX: Record<MybreNoticeChannel, string> = { board: 'gov:', volunteer: 'vol:' }

export type MybreNoticeFacts = Record<string, string | number | boolean | null>

export type MybreNoticeWire =
  | { noticeId: string; op: 'item'; sub: string; type: MybreNoticeType; facts: MybreNoticeFacts; link: string | null; subjectKey: string | null; endsAt: string | null }
  | { noticeId: string; op: 'done'; subjectKey: string }

export type MybreNotifyBody = { version: 1; notices: MybreNoticeWire[] }

export type MybreNoticeOutcome = 'stored' | 'duplicate' | 'done' | 'invalid' | 'simulated' | 'failed'
export type MybreNotifyAnswer = { results: { noticeId: string | null; status: MybreNoticeOutcome; reason?: string }[] }

// What core's claim hands the web Worker (sub resolved from the person's
// oldest active BRE Auth link; a person with none is skipped in core).
export type ClaimedMybreNotice = MybreNoticeWire & { id: string; channel: MybreNoticeChannel }

// What the web Worker reports back per notice.
export type MybreNoticeReport = { id: string; outcome: 'sent' | 'simulated' | 'skipped' | 'retry'; reason?: string }

const encoder = new TextEncoder()

function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

export function mybreNotifyCanonical(input: { path: string; channel: MybreNoticeChannel; timestamp: string; nonce: string; bodySha256: string }): string {
  return [MYBRE_NOTIFY_SIGNATURE_VERSION, 'POST', input.path, input.channel, input.timestamp, input.nonce, input.bodySha256].join('\n')
}

export async function signMybreNotify(input: { secret: string; channel: MybreNoticeChannel; body: string; now?: number; nonce?: string; path?: string }): Promise<Record<string, string>> {
  if (!input.secret || input.secret.length < 32) throw new Error('The channel secret must be at least 32 characters')
  const timestamp = String(Math.floor((input.now ?? Date.now()) / 1000))
  const nonce = input.nonce ?? base64url(crypto.getRandomValues(new Uint8Array(18)))
  const canonical = mybreNotifyCanonical({ path: input.path ?? MYBRE_NOTIFY_PATH, channel: input.channel, timestamp, nonce, bodySha256: await sha256Hex(input.body) })
  const key = await crypto.subtle.importKey('raw', encoder.encode(input.secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  const signature = base64url(new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(canonical))))
  return { [MYBRE_NOTIFY_HEADERS.channel]: input.channel, [MYBRE_NOTIFY_HEADERS.timestamp]: timestamp, [MYBRE_NOTIFY_HEADERS.nonce]: nonce, [MYBRE_NOTIFY_HEADERS.signature]: signature }
}

// The environment guard (ADR 0026's rule): a Hub that isn't production
// never sends to production myBRE, and production sends only there.
export function mybreNoticesTargetProblem(environment: string | undefined, url: string | undefined): string | null {
  if (!url) return 'MYBRE_NOTICES_URL is not set'
  let host = ''
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return 'MYBRE_NOTICES_URL must be https'
    host = parsed.hostname
  } catch {
    return 'MYBRE_NOTICES_URL is not a URL'
  }
  const production = environment === 'production'
  if (!production && host === 'loyalty.bre.live') return 'This Hub is not production but MYBRE_NOTICES_URL is production myBRE'
  if (production && host !== 'loyalty.bre.live') return 'Production Hub sends myBRE notices only to production myBRE'
  return null
}

// What myBRE's answer means for the outbox row.
export function reportFor(status: MybreNoticeOutcome | undefined, reason?: string): Omit<MybreNoticeReport, 'id'> {
  if (status === 'stored' || status === 'duplicate' || status === 'done') return { outcome: 'sent' }
  if (status === 'simulated') return { outcome: 'simulated', reason: 'myBRE has no inbox here' }
  if (status === 'invalid') return { outcome: 'skipped', reason: `myBRE refused it: ${reason ?? 'invalid'}`.slice(0, 300) }
  return { outcome: 'retry', reason: 'myBRE did not store it' }
}
